const CAMERA_DEVICE_LABELS_STORAGE_PREFIX = 'drawclub.sourceRecording.cameraDeviceLabels';

export interface CameraDeviceLabelStore {
  labels: Record<string, string>;
  nextLabelIndex: number;
}

export function getCameraDeviceKey(settings: Pick<MediaTrackSettings, 'deviceId'> | null | undefined, fallbackId: string) {
  const deviceId = typeof settings?.deviceId === 'string' ? settings.deviceId.trim() : '';
  return deviceId ? `device:${deviceId}` : `track:${fallbackId}`;
}

export function getCameraLetter(index: number) {
  let label = '';
  let value = Math.max(0, index);

  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);

  return label;
}

export function getCameraLabel(index: number) {
  return `Camera ${getCameraLetter(index)}`;
}

export function assignCameraDeviceLabel(
  store: CameraDeviceLabelStore,
  cameraDeviceKey: string
) {
  const existingLabel = store.labels[cameraDeviceKey];
  if (existingLabel) return existingLabel;

  const label = getCameraLetter(store.nextLabelIndex);
  store.labels[cameraDeviceKey] = label;
  store.nextLabelIndex += 1;
  return label;
}

export function getStoredCameraDeviceLabel(roomCode: string, cameraDeviceKey: string, fallbackIndex: number) {
  if (typeof window === 'undefined') return getCameraLabel(fallbackIndex);

  const storageKey = `${CAMERA_DEVICE_LABELS_STORAGE_PREFIX}:${roomCode}`;
  const store = readCameraDeviceLabelStore(storageKey);
  const label = assignCameraDeviceLabel(store, cameraDeviceKey);
  writeCameraDeviceLabelStore(storageKey, store);
  return `Camera ${label}`;
}

function readCameraDeviceLabelStore(storageKey: string): CameraDeviceLabelStore {
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return { labels: {}, nextLabelIndex: 0 };

    const parsed = JSON.parse(stored) as Partial<CameraDeviceLabelStore>;
    const nextLabelIndex = parsed.nextLabelIndex;
    return {
      labels: parsed.labels && typeof parsed.labels === 'object' ? parsed.labels : {},
      nextLabelIndex: typeof nextLabelIndex === 'number' && Number.isInteger(nextLabelIndex) ? nextLabelIndex : 0,
    };
  } catch {
    return { labels: {}, nextLabelIndex: 0 };
  }
}

function writeCameraDeviceLabelStore(storageKey: string, store: CameraDeviceLabelStore) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(store));
  } catch {
    // Local storage is a convenience for stable labels; recording can continue without it.
  }
}
